import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { GetAllPropertyforWeb } from "../APIs/APIHelperforWeb";
import { BaseURL } from "../APIs/APIUrls";

const formatDate = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().split("T")[0];
};

const toStringValue = (value) => {
  if (value === null || value === undefined) return undefined;
  return typeof value === "string" ? value.trim() : String(value);
};

const buildImageUrl = (url) => {
  if (!url) return undefined;
  const trimmed = url.toString().trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `${BaseURL}${trimmed.replace(/^\/+/, "")}`;
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const RealEstateListingSchema = () => {
  const location = useLocation();
  const [PropertyObject, setPropertyObject] = useState(null);

  useEffect(() => {
    const match = location.pathname.match(/^\/property-details\/([^/]+)/i);
    const slug = match?.[1] ? decodeURIComponent(match[1]) : "";

    if (!slug) {
      setPropertyObject(null);
      return;
    }

    const loadProperty = async () => {
      try {
        const cachedProperties = JSON.parse(localStorage.getItem("AllPropertiesforWeb") || "[]");
        const properties = Array.isArray(cachedProperties) && cachedProperties.length > 0
          ? cachedProperties
          : await GetAllPropertyforWeb();

        const foundProperty = Array.isArray(properties)
          ? properties.find((item) => {
              const autoUrl = toStringValue(item?.AutoGeneratedURL);
              const generatedId = toStringValue(item?.PropertyGeneratedId);
              return autoUrl === slug || generatedId === slug;
            })
          : null;

        setPropertyObject(foundProperty || null);
      } catch (error) {
        console.error("Unable to load property data for schema", error);
        setPropertyObject(null);
      }
    };

    loadProperty();
  }, [location.pathname]);

  if (!PropertyObject || Object.keys(PropertyObject).length === 0) {
    return null;
  }

  const title =
    toStringValue(PropertyObject.MetaTitle) ||
    toStringValue(PropertyObject.PropertyTitle) ||
    toStringValue(PropertyObject.PropertyGeneratedId) ||
    "Property Listing";

  const description =
    toStringValue(PropertyObject.MetaDescription) ||
    toStringValue(PropertyObject.PropertyDescription) ||
    toStringValue(PropertyObject.Description) ||
    "Find more details about this real estate listing on AskMe Realty.";

  const autoUrl = toStringValue(PropertyObject.AutoGeneratedURL);
  const pageUrl = autoUrl
    ? `https://askmerealty.in/property-details/${autoUrl}`
    : typeof window !== "undefined"
    ? window.location.href
    : "https://askmerealty.in/property-details";

  const images = Array.isArray(PropertyObject._lstPropertyImages)
    ? PropertyObject._lstPropertyImages
        .filter((img) => img && img.IsCard !== true)
        .map((img) => buildImageUrl(img.ImageURL || img.ImagePath || img.Image))
        .filter(Boolean)
    : [];

  const fallbackImage =
    buildImageUrl(PropertyObject.ImageURL || PropertyObject.PropertyImage || PropertyObject.Image) ||
    images[0];

  const offerPrice = parseNumber(
    PropertyObject.PropertyFor === "Rent" || PropertyObject.PropertyFor === "PG"
      ? PropertyObject.RentPerMonth
      : PropertyObject.PropertyFor === "Sell"
      ? PropertyObject.ExpectedPrice || PropertyObject.PropertyPrice
      : PropertyObject.ExpectedPrice || PropertyObject.RentPerMonth || PropertyObject.PropertyPrice,
  );

  const priceCurrency = "INR";
  const availability = "https://schema.org/InStock";

  const address = {
    streetAddress:
      toStringValue(PropertyObject.Address) ||
      [PropertyObject.Area, PropertyObject.AreaIncluding, PropertyObject.AddressLine]
        .filter(Boolean)
        .join(", "),
    addressLocality: toStringValue(PropertyObject.CityName) || toStringValue(PropertyObject.City),
    addressRegion: toStringValue(PropertyObject.StateName) || toStringValue(PropertyObject.State),
    postalCode:
      toStringValue(PropertyObject.PinCode) ||
      toStringValue(PropertyObject.Pincode) ||
      toStringValue(PropertyObject.ZipCode),
    addressCountry: toStringValue(PropertyObject.Country) || "IN",
  };

  const additionalProperty = [];
  const addPropertyValue = (name, value) => {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      additionalProperty.push({
        "@type": "PropertyValue",
        name,
        value: String(value),
      });
    }
  };

  addPropertyValue(
    "Bedrooms",
    PropertyObject.NoofRoom ||
      PropertyObject.NoofRoomId ||
      PropertyObject.BedRoomQty ||
      PropertyObject.BedRoomQuantity ||
      PropertyObject.BedRoom ||
      PropertyObject.Bedroom ||
      PropertyObject.Bedrooms,
  );
  addPropertyValue(
    "Bathrooms",
    PropertyObject.NoofWashroom ||
      PropertyObject.NoofWashroomId ||
      PropertyObject.BathRoomQty ||
      PropertyObject.BathRoomQuantity ||
      PropertyObject.BathRoom ||
      PropertyObject.Bathroom ||
      PropertyObject.Bathrooms,
  );
  addPropertyValue(
    "Property Type",
    PropertyObject.PropertyTypeName ||
      PropertyObject.PropertyType ||
      PropertyObject.BuildingTypeName ||
      PropertyObject.PropertyFor,
  );
  addPropertyValue(
    "Built-up Area",
    PropertyObject.BuiltUpArea && PropertyObject.BuildUpAreaUnitSize
      ? `${PropertyObject.BuiltUpArea} ${PropertyObject.BuildUpAreaUnitSize}`
      : PropertyObject.BuiltUpArea,
  );
  addPropertyValue(
    "Carpet Area",
    PropertyObject.CarpetArea && PropertyObject.CarpetAreaUnitSize
      ? `${PropertyObject.CarpetArea} ${PropertyObject.CarpetAreaUnitSize}`
      : PropertyObject.CarpetArea,
  );
  addPropertyValue(
    "Super Built-up Area",
    PropertyObject.SuperArea && PropertyObject.SuperAreaUnitSize
      ? `${PropertyObject.SuperArea} ${PropertyObject.SuperAreaUnitSize}`
      : PropertyObject.SuperArea,
  );
  addPropertyValue(
    "Land Size",
    PropertyObject.LandSize && PropertyObject.LandSizeUnit
      ? `${PropertyObject.LandSize} ${PropertyObject.LandSizeUnit}`
      : PropertyObject.LandSize,
  );
  addPropertyValue(
    "Furnishing Status",
    PropertyObject.FurnishingStatus ||
      PropertyObject.Furnishing ||
      PropertyObject.PropertyStatus ||
      PropertyObject.ConstructionStatus,
  );
  addPropertyValue("Floor Number", PropertyObject.PropertyOnTheFloor || PropertyObject.PropertyOnTheFloorId || PropertyObject.FloorNumber || PropertyObject.Floor || PropertyObject.FloorNo);
  addPropertyValue(
    "Ownership Status",
    PropertyObject.OwnershipStatus ||
      PropertyObject.OwnershipType ||
      PropertyObject.Ownership ||
      PropertyObject.OwnerShipType,
  );
  addPropertyValue("Facing", PropertyObject.Facing || PropertyObject.FacingId);
  addPropertyValue("Parking", PropertyObject.Parking || PropertyObject.ParkingId);
  addPropertyValue("Area", PropertyObject.Area || PropertyObject.AreaIncluding || PropertyObject.Address || PropertyObject.AddressLine);
  addPropertyValue("State", PropertyObject.StateName || PropertyObject.State);
  addPropertyValue("City", PropertyObject.CityName || PropertyObject.City);

  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: title,
    description,
    url: pageUrl,
    datePosted: formatDate(PropertyObject.CreatedOn || PropertyObject.CreatedDate || PropertyObject.DatePosted || PropertyObject.PostedOn),
    leaseLength:
      PropertyObject.OwnershipType || PropertyObject.Ownership || PropertyObject.OwnerShipType
        ? {
            "@type": "QuantitativeValue",
            name: "Ownership Type",
            value:
              toStringValue(PropertyObject.OwnershipType) ||
              toStringValue(PropertyObject.Ownership) ||
              toStringValue(PropertyObject.OwnerShipType),
          }
        : undefined,
    mainEntity: {
      "@type": "Product",
      name: title,
      description,
      image: images.length > 0 ? images : fallbackImage ? [fallbackImage] : undefined,
      offers: {
        "@type": "Offer",
        price: offerPrice !== undefined ? String(offerPrice) : undefined,
        priceCurrency,
        availability,
        eligibleRegion: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            ...address,
          },
        },
        priceValidUntil: formatDate(PropertyObject.PriceValidUntil || PropertyObject.PriceValidDate),
      },
      additionalProperty: additionalProperty.length > 0 ? additionalProperty : undefined,
    },
  };

  const compactSchema = JSON.parse(JSON.stringify(schema));

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(compactSchema)}</script>
    </Helmet>
  );
};

export default RealEstateListingSchema;
